<template>
  <Teleport to="body">
    <Transition name="popover-fade">
      <div
        v-if="show"
        ref="popoverRef"
        class="read-receipts-popover"
        :style="popoverStyle"
        @click.stop
      >
        <div class="read-receipts-popover__arrow" :style="arrowStyle"></div>
        <h4 class="read-receipts-popover__title">Read Receipts</h4>
        <div v-if="readReceipts && readReceipts.length > 0" class="read-receipts-popover__list">
          <div
            v-for="(receipt, index) in readReceipts"
            :key="receipt.id || index"
            class="read-receipts-popover__item"
          >
            <div class="read-receipts-popover__recipient">
              {{ receipt.recipientEmail || "Unknown recipient" }}
            </div>
            <div class="read-receipts-popover__event-info">
              <span class="read-receipts-popover__event-type">
                {{
                  receipt.eventType?.toUpperCase() === "EMAIL_CLICKED" ? "Link Clicked" : "Opened"
                }}
              </span>
              <span class="read-receipts-popover__separator"> • </span>
              <span class="read-receipts-popover__timestamp">{{
                receipt.formattedDate ||
                (receipt.readAt || receipt.eventTimestamp
                  ? formatTimestamp(receipt.readAt || receipt.eventTimestamp!)
                  : "")
              }}</span>
            </div>
            <div
              v-if="receipt.eventType === 'EMAIL_CLICKED' && receipt.url"
              class="read-receipts-popover__clicked-url"
            >
              <Icon icon="mdi:link" />
              <span>{{ receipt.url }}</span>
            </div>
          </div>
        </div>
        <div v-else class="read-receipts-popover__empty">
          <LoadingBars v-if="isLoading" size="sm" :show-text="false" />
          <p v-else>No read receipts yet</p>
        </div>
      </div>
    </Transition>
    <div v-if="show" class="read-receipts-popover__backdrop" @click="$emit('close')"></div>
  </Teleport>
</template>

<script setup lang="ts">
import { Teleport, nextTick, onUnmounted, ref, watch } from "vue";
import { Icon } from "@iconify/vue";
import type { ContactEventHistory } from "@/dtos/Unipile.dto";
import LoadingBars from "@/components/ui/LoadingBars.vue";

interface ReadReceipt {
  id?: string;
  readAt?: string;
  eventTimestamp?: string;
  readCount?: number;
  formattedDate?: string;
  [key: string]: any;
}

interface Props {
  show: boolean;
  readReceipts?: ReadReceipt[];
  readCount?: number;
  triggerElement?: HTMLElement | null;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readReceipts: (): ReadReceipt[] => [],
  readCount: 0,
  triggerElement: null,
  isLoading: false,
});

const emit = defineEmits<{
  close: [];
}>();

const popoverRef = ref<HTMLElement | null>(null);
const popoverStyle = ref<Record<string, string>>({});
const arrowStyle = ref<Record<string, string>>({});

const formatTimestamp = (timestamp: string): string => {
  if (!timestamp) return "";
  // Format: "Tue, Dec 23, 2025 at 01:41 PM"
  const date = new Date(timestamp);
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const weekday = weekdays[date.getDay()];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
  return `${weekday}, ${month} ${day}, ${year} at ${hours}:${minutesStr} ${ampm}`;
};

const getReadText = (readCount: number): string => {
  return readCount === 1 ? "Read 1 time" : `Read ${readCount} times`;
};

const updatePosition = () => {
  if (!props.triggerElement || !popoverRef.value) return;

  const triggerRect = props.triggerElement.getBoundingClientRect();
  const popoverRect = popoverRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top = triggerRect.bottom + 8;
  let left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
  let arrowTop = -8;
  let arrowLeft = "50%";
  let transform = "translateX(-50%)";

  if (left < 8) {
    left = 8;
    arrowLeft = `${triggerRect.left + triggerRect.width / 2 - 8}px`;
    transform = "none";
  } else if (left + popoverRect.width > viewportWidth - 8) {
    left = viewportWidth - popoverRect.width - 8;
    arrowLeft = `${triggerRect.right - (viewportWidth - popoverRect.width - 8)}px`;
    transform = "none";
  }

  if (top + popoverRect.height > viewportHeight - 8) {
    top = triggerRect.top - popoverRect.height - 8;
    arrowTop = popoverRect.height;
    arrowStyle.value = {
      top: `${arrowTop}px`,
      left: arrowLeft,
      transform,
      borderTop: "8px solid #ffffff",
      borderBottom: "none",
    };
  } else {
    arrowStyle.value = {
      top: `${arrowTop}px`,
      left: arrowLeft,
      transform,
      borderBottom: "8px solid #ffffff",
      borderTop: "none",
    };
  }

  popoverStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
  };
};

watch(
  () => props.show,
  async (isShowing) => {
    if (isShowing) {
      await nextTick();
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    } else {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  window.removeEventListener("scroll", updatePosition, true);
  window.removeEventListener("resize", updatePosition);
});
</script>

<style scoped>
.read-receipts-popover {
  position: fixed;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 0 16px 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  max-width: 400px;
  z-index: 11000;
  overflow: hidden;
}

.read-receipts-popover__arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid #ffffff;
}

.read-receipts-popover__title {
  margin: 0 0 10px 0;
  padding-right: 20px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.01em;
}

.read-receipts-popover__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 280px;
  overflow-y: auto;
}

.read-receipts-popover__item {
  font-size: 0.75rem;
  color: #4c4f6a;
  line-height: 1.5;
  padding: 8px 20px 8px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-bottom: 1px solid #e5e7eb;
}

.read-receipts-popover__item:last-child {
  border-bottom: none;
}

.read-receipts-popover__recipient {
  font-weight: 600;
  color: #1a1a2e;
  font-size: 0.8125rem;
}

.read-receipts-popover__event-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
}

.read-receipts-popover__event-type {
  font-weight: 500;
  color: #3e3efe;
}

.read-receipts-popover__read-count {
  font-weight: 500;
  color: #1a1a2e;
}

.read-receipts-popover__separator {
  color: #9ca3af;
  margin: 0 4px;
}

.read-receipts-popover__timestamp {
  color: #6b7280;
}

.read-receipts-popover__clicked-url {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.6875rem;
  color: #6b7280;
  word-break: break-all;
  margin-top: 2px;
}

.read-receipts-popover__clicked-url .iconify {
  font-size: 14px;
  color: #3b82f6;
  flex-shrink: 0;
}

.read-receipts-popover__empty {
  padding: 8px 0;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}

.read-receipts-popover__backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10999;
  background: transparent;
}

.popover-fade-enter-active,
.popover-fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.popover-fade-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}

.popover-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
